import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient'
import {
  DateRange,
  FiltersDefinition,
  FiltersState,
  OneDataCollection,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import { AnalyticsSnapshotResultsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import {
  datatableFiltersToJsonLogicFilters,
  filterDefinitionsFromSchema,
} from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { formatColumns } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'
import { ReportSchemaType } from 'modules/ai_reports/lib/types'

import type { ReportTableAction, ReportTableProps } from './index'

interface SnapshotTableContentProps {
  id: string
  sqlQuery: string
  tableId: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ReportTableProps['columnFormatHints']
  onStateChange: ReportTableProps['onStateChange']
  dateFilter?: DateRange
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  currentFilters: FiltersState<FiltersDefinition<string>> | undefined
  currentSchema: ReportSchemaType
  onSchemaChanged: (schema: ReportSchemaType) => void
}

const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 10

const SnapshotTableContent = ({
  id,
  sqlQuery,
  tableId,
  actions,
  columnFormatHints,
  onStateChange,
  dateFilter,
  onUserFacingSqlQueryChange,
  currentFilters,
  currentSchema,
  onSchemaChanged,
}: SnapshotTableContentProps) => {
  const client = useApolloClient()
  const { company, access } = useCurrentSession()

  const source = useDataCollectionSource(
    {
      filters: filterDefinitionsFromSchema(currentSchema),
      currentFilters: currentFilters,
      primaryActions: () => actions?.primary,
      secondaryActions: actions?.secondary
        ? {
            expanded: 1,
            actions: () => actions?.secondary ?? [],
          }
        : undefined,
      dataAdapter: {
        paginationType: 'pages',
        perPage: INITIAL_PAGE_SIZE,
        fetchData: (options) => {
          const pagination = options.pagination
          const perPage = pagination.perPage ?? INITIAL_PAGE_SIZE
          const currentPage = pagination.currentPage ?? INITIAL_PAGE

          const snapshotDate = dateFilter?.from ?? new Date()

          const filters = options.filters
          const jsonLogicFilters = datatableFiltersToJsonLogicFilters(filters, currentSchema)

          return client
            .watchQuery({
              query: AnalyticsSnapshotResultsDocument,
              fetchPolicy: 'cache-and-network',
              variables: {
                query: sqlQuery,
                companyId: company.id,
                snapshotDate: snapshotDate.toISOString(),
                page: currentPage,
                perPage,
                tableId,
                filters: JSON.stringify(jsonLogicFilters),
              },
              notifyOnNetworkStatusChange: false,
              nextFetchPolicy: 'cache-and-network',
            })
            .map(({ data, loading, error }) => {
              onUserFacingSqlQueryChange(
                data?.iris?.snapshotResultsConnection?.nodes?.[0]?.sqlQuery ?? ''
              )
              onSchemaChanged(data?.iris?.snapshotResultsConnection?.nodes?.[0]?.schema ?? [])
              return {
                loading: loading,
                error: error,
                data: {
                  records:
                    data?.iris.snapshotResultsConnection.nodes.map((node) =>
                      Object.fromEntries(node.values.map(({ name, value }) => [name, value ?? '']))
                    ) ?? [],
                  total: data?.iris.snapshotResultsConnection.totalCount ?? 0,
                  currentPage: currentPage,
                  pagesCount: Math.ceil(
                    (data?.iris.snapshotResultsConnection.totalCount ?? 0) / perPage
                  ),
                  perPage: perPage,
                  type: 'pages',
                },
              }
            })
        },
      },
    },
    [sqlQuery, company.id, dateFilter, currentSchema]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      id={`snapshot-table-${id}/v1`}
      storage={false}
      onStateChange={onStateChange}
      visualizations={[
        {
          type: 'table',
          options: {
            allowColumnReordering: true,
            allowColumnHiding: true,
            frozenColumns: 1,
            columns: formatColumns(currentSchema, columnFormatHints, access.locale),
          },
        },
      ]}
    />
  )
}

export default SnapshotTableContent
